// components/common/dropdowns/InlineSelectField.tsx
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import LabeledSelectDropdown from './Labeledselectdropdown';

interface Option<T extends string> {
  value: T;
  label: string;
  description?: string;
}

interface InlineSelectFieldProps<T extends string> {
  label?: string;
  options: readonly Option<T>[];
  value: T | undefined;
  /** PATCH the changed field. Return the promise (mutateAsync) so we can roll back. */
  onSave: (next: T) => Promise<unknown>;
  placeholder?: string;
  disabled?: boolean;
  errorMessage?: string;
  className?: string;
}

/** LabeledSelectDropdown that PATCHes on select instead of bubbling onChange up. */
function InlineSelectField<T extends string>({
  label,
  options,
  value,
  onSave,
  placeholder,
  disabled,
  errorMessage = 'Could not save your change. Please try again.',
  className,
}: InlineSelectFieldProps<T>) {
  // Shows the picked value immediately; cleared if the PATCH fails.
  const [pending, setPending] = useState<T | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Once the refetched value catches up, hand control back to the prop.
  useEffect(() => {
    if (pending !== null && value === pending) setPending(null);
  }, [value, pending]);

  const handleChange = async (next: T) => {
    if (next === (pending ?? value)) return;

    setPending(next);
    setIsSaving(true);
    try {
      await onSave(next);
    } catch (err: any) {
      setPending(null);
      toast.error(err?.message ?? errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <LabeledSelectDropdown<T>
      label={label}
      options={options}
      value={pending ?? value}
      placeholder={placeholder}
      onChange={handleChange}
      disabled={disabled}
      loading={isSaving}
      className={className}
    />
  );
}

export default InlineSelectField;